import { Component, inject, input, output, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { form, FormField, required } from '@angular/forms/signals';
import { finalize } from 'rxjs';
import { LoginIssuerStore } from '../../login-issuer.store';
import {
  InternalAuthApiService,
  InternalAuthenticationService,
  LoginResult,
} from '@ups/xplat/base/auth';
import { ButtonModule } from '@progress/kendo-angular-buttons';

@Component({
  selector: 'ups-internal-login',
  templateUrl: 'login.component.html',
  imports: [FormField, ButtonModule],
})
export class LoginComponent {
  auth = inject(InternalAuthenticationService);
  authApi = inject(InternalAuthApiService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  loginIssuerStore = inject(LoginIssuerStore);

  useDifferentProvider = output();

  forgotPassScreen = signal(false);

  identifier = input();

  loginModel = signal({
    email: '',
    password: '',
  });

  loginForm = form(this.loginModel, (x) => {
    required(x.email);
    required(x.password, { when: () => !this.forgotPassScreen() });
  });

  isLoading = signal(false);
  result = signal<
    | { type: 'result'; for: 'login'; result: LoginResult }
    | { type: 'result'; for: 'forgot'; result: boolean }
    | { type: 'error'; error: unknown }
    | null
  >(null);
  loginResultEnum = LoginResult;

  submit(event: Event) {
    event.preventDefault();

    if (!this.forgotPassScreen()) {
      this.doLogin();
    } else {
      this.doForgotPass();
    }
  }

  doLogin() {
    const loginForm = this.loginForm();
    if (!loginForm.valid()) return;

    this.result.set(null);
    this.isLoading.set(true);
    this.auth
      .login(loginForm.value())
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (x) => {
          this.result.set({ type: 'result', for: 'login', result: x });

          if (x === LoginResult.Success) {
            this.loginIssuerStore.set('internal');
            this.redirectBack();
          }
        },
        error: (x) => this.result.set({ type: 'error', error: x }),
      });
  }

  doForgotPass() {
    const loginForm = this.loginForm();
    if (!loginForm.valid()) return;

    this.result.set(null);
    this.isLoading.set(true);
    this.authApi
      .requestPasswordReset({ email: loginForm.value().email })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (x) =>
          this.result.set({ type: 'result', for: 'forgot', result: x.success }),
        error: (x) => {
          console.error('Password reset request error', x);
          this.result.set({ type: 'error', error: x });
        },
      });
  }

  redirectBack() {
    // 'returnTo' — matches the issuer-aware guard and DualLoginComponent so the
    // post-login redirect target set by the guard is honored after internal login.
    const route = this.route.snapshot.queryParamMap.get('returnTo') ?? '/';
    this.isLoading.set(true);
    // navigateByUrl so a returnTo carrying a nested path and/or query string
    // resolves as a full URL rather than a single path segment.
    this.router.navigateByUrl(route);
  }
}
